/**
 * Performance Rating Distribution Component (CR-6381)
 *
 * Displays a horizontal bar chart showing the distribution of suppliers
 * across rating bands (A/B/C/D/F/N/A). Bars are clickable to drill down
 * to the list of suppliers with that rating.
 */
import { useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'

/** Rating band colors matching severity (green=good, red=bad, gray=N/A) */
const RATING_COLORS: Record<string, string> = {
  A: '#22c55e', // green-500
  B: '#84cc16', // lime-500
  C: '#eab308', // yellow-500
  D: '#f97316', // orange-500
  F: '#ef4444', // red-500
  'N/A': '#9ca3af', // gray-400
}

/** Ordered list of ratings for consistent display */
const RATING_ORDER = ['A', 'B', 'C', 'D', 'F', 'N/A']

interface RatingBarProps {
  rating: string
  count: number
  maxCount: number
  onBarClick?: (rating: string) => void
}

function RatingBar({ rating, count, maxCount, onBarClick }: RatingBarProps) {
  const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0
  const color = RATING_COLORS[rating] || '#9ca3af'
  const isClickable = !!onBarClick && count > 0

  const handleClick = useCallback(() => {
    if (onBarClick && count > 0) {
      onBarClick(rating)
    }
  }, [onBarClick, rating, count])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && onBarClick && count > 0) {
        onBarClick(rating)
      }
    },
    [onBarClick, rating, count]
  )

  return (
    <div className="flex items-center gap-3 mb-2">
      {/* Rating label */}
      <div className="w-10 text-sm font-medium text-right">{rating}</div>

      {/* Bar container */}
      <div className="flex-1 h-7 bg-gray-100 rounded overflow-hidden">
        <div
          className={`h-full flex items-center transition-all duration-300 ${
            isClickable ? 'cursor-pointer hover:brightness-110' : ''
          }`}
          style={{
            width: `${Math.max(percentage, count > 0 ? 8 : 0)}%`,
            minWidth: count > 0 ? '32px' : '0',
            backgroundColor: color,
          }}
          onClick={handleClick}
          role={isClickable ? 'button' : undefined}
          tabIndex={isClickable ? 0 : undefined}
          onKeyDown={handleKeyDown}
          title={isClickable ? `Click to view ${count} supplier(s) with rating ${rating}` : undefined}
        >
          {count > 0 && (
            <span className="px-2 text-xs font-medium text-white">{count}</span>
          )}
        </div>
      </div>

      {/* Count label (for accessibility when bar is too small) */}
      <div className="w-8 text-sm text-muted-foreground text-right">{count}</div>
    </div>
  )
}

interface PerformanceRatingDistributionProps {
  /** Distribution data as {rating: count} */
  data: Record<string, number>
  /** Title for the chart card */
  title: string
  /** Callback when a rating bar is clicked */
  onBarClick?: (rating: string) => void
}

export function PerformanceRatingDistribution({
  data,
  title,
  onBarClick,
}: PerformanceRatingDistributionProps) {
  // Calculate max count for scaling bars
  const maxCount = Math.max(...Object.values(data), 1)

  // Get total suppliers
  const totalSuppliers = Object.values(data).reduce((sum, count) => sum + count, 0)

  // Filter to only ratings that exist in configuration
  const ratings = RATING_ORDER.filter((r) => r in data || data[r] !== undefined)

  if (ratings.length === 0 || totalSuppliers === 0) {
    return (
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No rating distribution data available
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle className="text-base">{title}</CardTitle>
          <span className="text-xs text-muted-foreground">
            {totalSuppliers} supplier{totalSuppliers !== 1 ? 's' : ''}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {ratings.map((rating) => (
            <RatingBar
              key={rating}
              rating={rating}
              count={data[rating] || 0}
              maxCount={maxCount}
              onBarClick={onBarClick}
            />
          ))}
        </div>

        {/* Legend */}
        <div className="mt-4 pt-3 border-t flex flex-wrap gap-3 text-xs text-muted-foreground justify-center">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: RATING_COLORS['A'] }} />
            <span>Excellent</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: RATING_COLORS['B'] }} />
            <span>Good</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: RATING_COLORS['C'] }} />
            <span>Fair</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: RATING_COLORS['D'] }} />
            <span>Poor</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: RATING_COLORS['F'] }} />
            <span>Failing</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: RATING_COLORS['N/A'] }} />
            <span>No Data</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
